<template>
    <div>
        <!-- Datatable list of speaker sessions -->
        <datatable :isLoading.sync="datatable.isLoading"
                   :columns="datatable.columns"
                   :rows="datatable.rows"
                   :total="datatable.total"
                   :queryParams="datatable.queryParams"
                   :lineNumbers="false"
                   @on-page-change="onPageChange"
                   @on-sort-change="onSortChange"
                   @on-per-page-change="onPerPageChange"
                   v-show="datatable.showTable"
        />

        <!-- Quick Apply Modal -->
        <CommonModal ref="quickApplyModal" size="lg" :onHideOnlyX="true">
            <template v-slot:CommonModalTitle>
                {{ selectedSession ? selectedSession.day_label : '' }}
            </template>
            <template v-slot:CommonModalContent>
                <div class="p-3" v-if="selectedSession">
                    <!-- Slots Selection Grid -->
                    <b-row class="mb-4 text-center">
                        <b-col v-for="(group, idx) in groupedSlots" :key="idx" cols="3" class="px-2">
                            <b-button v-for="slot in group"
                                      :key="slot"
                                      size="sm"
                                      block
                                      :variant="getSlotVariant(slot)"
                                      :disabled="isSlotBooked(slot)"
                                      class="mb-2 py-2 font-weight-bold slot-btn shadow-sm"
                                      :style="getSlotStyle(slot)"
                                      @click="selectSlot(slot)">
                                {{ formatSlotText(slot) }}
                            </b-button>
                        </b-col>
                    </b-row>

                    <!-- CV Selection -->
                    <b-row class="mb-4">
                        <b-col md="6">
                            <b-form-group :label="$t('coop_select_cv')" class="font-weight-bold text-center">
                                <b-form-select v-model="selectedCv" :options="cvOptions">
                                    <template #first>
                                        <b-form-select-option :value="null" disabled>-- {{ $t('coop_select_cv') }} --</b-form-select-option>
                                    </template>
                                </b-form-select>
                            </b-form-group>
                        </b-col>
                    </b-row>

                    <!-- Save Action -->
                    <div class="text-center">
                        <b-button variant="primary"
                                  class="px-5 font-weight-bold shadow"
                                  style="background-color: #007bff; border-color: #007bff;"
                                  @click="submitBooking">
                            {{ $t('save') }}
                        </b-button>
                    </div>
                </div>
            </template>
        </CommonModal>
    </div>
</template>

<script>
    import Datatable from '@/components/datatable/Datatable';
    import CommonModal from '@/components/elements/CommonModal';
    import CoopBusinessPointService from '@/services/CoopBusinessPointService';
    import CoopCvService from '@/services/CoopCvService';
    import qs from 'qs';

    export default {
        name: 'BusinessPoint',
        components: {
            Datatable,
            CommonModal
        },
        data() {
            return {
                cvList: [],
                allSlots: [],
                selectedSession: null,
                selectedSlot: null,
                selectedCv: null,
                datatable: {
                    isLoading: false,
                    columns: [
                        {
                            label: '',
                            field: 'buttons',
                            html: true,
                            hidden: false,
                            tdClass: 'p-0 text-center w-40 align-middle',
                            buttons: [
                                {
                                    text: this.$t('coop_quick_apply'),
                                    class: 'ri-flashlight-line text-warning',
                                    callback: (row) => {
                                        this.openQuickApplyModal(row);
                                    }
                                }
                            ]
                        },
                        {
                            label: this.toUpperCase('coop_company'),
                            field: 'company_name',
                            hidden: false
                        },
                        {
                            label: this.toUpperCase('coop_speaker'),
                            field: 'speaker',
                            hidden: false
                        },
                        {
                            label: this.toUpperCase('coop_duration'),
                            field: 'duration',
                            hidden: false
                        },
                        {
                            label: this.toUpperCase('coop_remaining_quota'),
                            field: 'quota',
                            hidden: false
                        },
                        {
                            label: this.toUpperCase('coop_application_date'),
                            field: 'date',
                            hidden: false
                        }
                    ],
                    rows: [],
                    total: 0,
                    showTable: false,
                    queryParams: {
                        filter: {},
                        sort: 'id',
                        page: 1,
                        limit: 20
                    }
                }
            };
        },
        computed: {
            cvOptions() {
                return this.cvList.map(cv => ({ value: cv.id, text: cv.name }));
            },
            groupedSlots() {
                // Group slots into 4 columns exactly matching the wireframe:
                // Col 1: 10:00-11:00
                // Col 2: 11:30-12:30
                // Col 3: 13:30-14:30
                // Col 4: 15:00-16:00
                const col1 = this.allSlots.filter(s => s.startsWith('10:'));
                const col2 = this.allSlots.filter(s => s.startsWith('11:') || s.startsWith('12:'));
                const col3 = this.allSlots.filter(s => s.startsWith('13:') || s.startsWith('14:'));
                const col4 = this.allSlots.filter(s => s.startsWith('15:'));
                return [col1, col2, col3, col4];
            }
        },
        methods: {
            onPageChange(params) {
                this.datatable.queryParams.page = params.currentPage;
                this.getRows();
            },
            onPerPageChange(params) {
                this.datatable.queryParams.limit = params.currentPerPage;
                this.datatable.queryParams.page = 1;
                this.getRows();
            },
            onSortChange(params) {
                const sortType = params[0].type == 'desc' ? '-' : '';
                this.datatable.queryParams.sort = sortType + params[0].field;
                this.getRows();
            },
            getRows() {
                this.datatable.showTable = true;
                this.datatable.isLoading = true;

                const config = {
                    params: {
                        ...this.datatable.queryParams
                    },
                    paramsSerializer: (params) => qs.stringify(params, { encode: false })
                };

                return CoopBusinessPointService.getAll(config)
                    .then((response) => {
                        this.datatable.rows = response.data.data;
                        this.datatable.total = response.data.pagination.total;
                    })
                    .finally(() => {
                        this.datatable.isLoading = false;
                    });
            },
            openQuickApplyModal(row) {
                this.selectedSession = row;
                this.selectedSlot = null;
                this.selectedCv = null;
                this.$refs.quickApplyModal.$refs.commonModal.show();
            },
            isSlotBooked(slot) {
                if (!this.selectedSession) return false;
                // Standardize formats (remove extra spaces if any)
                const normalize = s => s.replace(/\s+/g, '');
                return this.selectedSession.booked_slots.some(b => normalize(b) === normalize(slot));
            },
            selectSlot(slot) {
                this.selectedSlot = slot;
            },
            getSlotVariant(slot) {
                if (this.isSlotBooked(slot)) {
                    return 'secondary';
                }
                if (this.selectedSlot === slot) {
                    return 'warning'; // Orange / yellow highlight matching wireframe
                }
                return 'outline-dark';
            },
            getSlotStyle(slot) {
                if (this.isSlotBooked(slot)) {
                    return {
                        opacity: '0.4',
                        cursor: 'not-allowed',
                        textDecoration: 'line-through'
                    };
                }
                if (this.selectedSlot === slot) {
                    return {
                        backgroundColor: '#fd7e14', // Vibrant orange
                        borderColor: '#fd7e14',
                        color: '#ffffff'
                    };
                }
                return {
                    borderColor: '#ced4da',
                    color: '#495057',
                    backgroundColor: '#ffffff'
                };
            },
            formatSlotText(slot) {
                // Replaces standard '10:00 - 10:15' with '10.00-10.15'
                return slot.replace(/\s+/g, '').replace(/:/g, '.');
            },
            submitBooking() {
                if (!this.selectedSlot) {
                    this.$toast.error(this.$t('coop_please_select_time'));
                    return;
                }
                if (!this.selectedCv) {
                    this.$toast.error(this.$t('coop_please_select_cv'));
                    return;
                }

                CoopBusinessPointService.apply(this.selectedSession.id, this.selectedSlot, this.selectedCv).then(() => {
                    this.$toast.success(this.$t('coop_application_sent_success'));
                    this.$refs.quickApplyModal.$refs.commonModal.hide();
                    this.getRows();
                });
            }
        },
        created() {
            this.getRows();
            CoopBusinessPointService.getSlots().then((res) => {
                this.allSlots = res.data;
            });
            CoopCvService.getAll().then((res) => {
                this.cvList = res.data.data;
            });
        }
    }
</script>

<style scoped>
    .slot-btn {
        transition: all 0.2s ease-in-out;
    }
    .slot-btn:hover:not(:disabled) {
        transform: scale(1.02);
    }
</style>
